import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, CircularProgress, Typography, Paper, Button, TextField, IconButton, Tooltip,
  Snackbar, Alert, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, useTheme, useMediaQuery,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ClearIcon from "@mui/icons-material/Clear";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchAttendanceMonthly } from "../../actions/AttendanceDetailsAction";
import AttendanceDetailsService from "../../service/AttendanceDetailsService";

const NAVY = "#154c79";
const MAX_DAYS = 31; // longest range allowed (the grid has one column per day)

// ── date helpers (local time, no UTC shift) ──
const pad = (n) => String(n).padStart(2, "0");
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromKey = (k) => new Date(`${k}T00:00:00`);
const monthStart = () => {
  const n = new Date();
  return toKey(new Date(n.getFullYear(), n.getMonth(), 1));
};
const isSunday = (k) => fromKey(k).getDay() === 0;
const WEEKDAY = ["S", "M", "T", "W", "T", "F", "S"];

// API date "10/21/2026 12:00:00 AM" (M/D/YYYY) -> "2026-10-21"
const apiDateToKey = (s) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(s || ""));
  return m ? `${m[3]}-${pad(m[1])}-${pad(m[2])}` : "";
};

const buildDays = (from, to) => {
  const out = [];
  if (!from || !to) return out;
  for (let d = fromKey(from); d <= fromKey(to); d.setDate(d.getDate() + 1)) out.push(toKey(d));
  return out;
};

const headSx = { backgroundColor: NAVY, color: "#fff", fontWeight: 600, fontSize: 11, p: "6px", whiteSpace: "nowrap", textAlign: "center" };

export default function AttendanceMonthly() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { loading, data, error } = useSelector(
    (s) => s.attendanceMonthly || { loading: false, data: [], error: null }
  );

  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(toKey(new Date()));
  const [loc, setLoc] = useState(""); // "" = all locations
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const days = useMemo(() => buildDays(fromDate, toDate), [fromDate, toDate]);

  const rangeError = !fromDate || !toDate
    ? "Select both dates"
    : fromDate > toDate
    ? "From date must be on or before To date"
    : days.length > MAX_DAYS
    ? `Select ${MAX_DAYS} days or fewer`
    : "";

  // location dropdown
  useEffect(() => {
    AttendanceDetailsService.getLocations()
      .then(setLocations)
      .catch((e) => console.error("Failed to load locations:", e));
  }, []);

  const locationOptions = useMemo(
    () =>
      locations
        .map((l) => ({
          code: String(l.locCode || l.LocCode || "").trim(),
          name: String(l.locName || l.LocName || "").trim(),
        }))
        .filter((l) => l.code)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [locations]
  );

  // load data whenever the range or location changes
  useEffect(() => {
    if (!rangeError) dispatch(fetchAttendanceMonthly(fromDate, toDate, loc));
  }, [dispatch, fromDate, toDate, loc, rangeError]);

  // one row per employee, with their days keyed by date
  const employees = useMemo(() => {
    const map = new Map();
    (Array.isArray(data) ? data : []).forEach((r) => {
      const sno = String(r.serviceNo ?? "").trim();
      if (!sno) return;
      if (!map.has(sno)) {
        map.set(sno, { serviceNo: sno, name: r.name || "", locName: r.locName || r.location || "", days: {} });
      }
      const key = apiDateToKey(r.date);
      if (key) map.get(sno).days[key] = { inTime: String(r.inTime || "").trim(), outTime: String(r.outTime || "").trim() };
    });
    return Array.from(map.values())
      .map((e) => ({ ...e, present: Object.values(e.days).filter((d) => d.inTime).length }))
      .sort((a, b) => a.locName.localeCompare(b.locName) || a.name.localeCompare(b.name));
  }, [data]);

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter(
      (e) =>
        e.serviceNo.toLowerCase().includes(term) ||
        e.name.toLowerCase().includes(term) ||
        e.locName.toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  const totalPresentDays = useMemo(
    () => filteredEmployees.reduce((s, e) => s + e.present, 0),
    [filteredEmployees]
  );

  const locLabel = loc ? locationOptions.find((l) => l.code === loc)?.name || loc : "All Locations";

  const handleRefresh = () => {
    if (rangeError) return;
    dispatch(fetchAttendanceMonthly(fromDate, toDate, loc));
    showSnackbar("Data refreshed", "success");
  };

  // ── PDF ──
  const handleExportPDF = () => {
    if (exporting || filteredEmployees.length === 0 || rangeError) return;
    setExporting(true);
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 8;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Monthly Attendance Report", pageWidth / 2, 12, { align: "center" });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Period: ${fromDate} to ${toDate}   |   Location: ${locLabel}`, margin, 19);
      doc.text(
        `Employees: ${filteredEmployees.length}   |   Present days: ${totalPresentDays}   |   Generated: ${new Date().toLocaleString()}`,
        margin, 24
      );

      const showLoc = !loc;
      const fixedCols = ["Service No", "Name", ...(showLoc ? ["Location"] : [])];
      const head = [[...fixedCols, ...days.map((k) => `${Number(k.slice(8))}\n${WEEKDAY[fromKey(k).getDay()]}`), "Days"]];
      const body = filteredEmployees.map((e) => [
        e.serviceNo,
        e.name,
        ...(showLoc ? [e.locName] : []),
        ...days.map((k) => {
          const d = e.days[k];
          if (!d) return "";
          return d.inTime ? `${d.inTime}\n${d.outTime || "-"}` : "-";
        }),
        e.present,
      ]);

      const fixedW = [17, 38, ...(showLoc ? [24] : [])];
      const totalW = 9;
      const usable = pageWidth - margin * 2;
      const dayW = (usable - fixedW.reduce((a, b) => a + b, 0) - totalW) / Math.max(days.length, 1);
      const columnStyles = {};
      fixedW.forEach((w, i) => { columnStyles[i] = { cellWidth: w, halign: i === 0 ? "center" : "left" }; });
      days.forEach((_, i) => { columnStyles[fixedW.length + i] = { cellWidth: dayW }; });
      columnStyles[fixedW.length + days.length] = { cellWidth: totalW, fontStyle: "bold" };

      autoTable(doc, {
        head,
        body,
        startY: 28,
        margin: { left: margin, right: margin },
        tableWidth: usable,
        styles: { fontSize: 5.5, cellPadding: 0.6, halign: "center", valign: "middle", lineWidth: 0.1, lineColor: [210, 218, 230] },
        headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold", fontSize: 6 },
        columnStyles,
        didParseCell: (hook) => {
          const idx = hook.column.index - fixedW.length;
          if (idx >= 0 && idx < days.length && isSunday(days[idx])) {
            if (hook.section === "body") hook.cell.styles.fillColor = [255, 243, 224];
            if (hook.section === "head") hook.cell.styles.fillColor = [180, 83, 9];
          }
        },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 5, { align: "right" });
      }
      doc.save(`MonthlyAttendance_${fromDate}_to_${toDate}${loc ? `_${loc}` : ""}.pdf`);
      showSnackbar("PDF exported successfully!", "success");
    } catch (err) {
      console.error("PDF error:", err);
      showSnackbar("Failed to export PDF", "error");
    }
    setExporting(false);
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: NAVY } } };

  return (
    <Box
      sx={{
        width: 0,
        minWidth: "100%",
        boxSizing: "border-box",
        p: isMobile ? "0 8px" : "8px 12px",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Title */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#0e3c66", flex: 1 }}>
          📅 Monthly Attendance
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={handleRefresh} disabled={loading || !!rangeError} size="small" sx={{ color: NAVY }}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Controls */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          type="date" size="small" label="From" value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, minWidth: 150 }}
        />
        <TextField
          type="date" size="small" label="To" value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, minWidth: 150 }}
        />
        <TextField
          select size="small" label="Location" value={loc}
          onChange={(e) => setLoc(e.target.value)}
          sx={{ ...fieldSx, minWidth: 200 }}
        >
          <MenuItem value="">All Locations</MenuItem>
          {locationOptions.map((l) => (
            <MenuItem key={l.code} value={l.code}>{l.name || l.code}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" placeholder="Search name, service no..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />,
            endAdornment: searchTerm ? (
              <IconButton size="small" onClick={() => setSearchTerm("")}><ClearIcon fontSize="small" /></IconButton>
            ) : null,
          }}
          sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
        <Button
          variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF}
          disabled={exporting || filteredEmployees.length === 0 || !!rangeError}
          sx={{ height: 40, borderRadius: "8px", borderColor: NAVY, color: NAVY, textTransform: "none", whiteSpace: "nowrap" }}
        >
          {exporting ? "Exporting..." : "PDF"}
        </Button>
      </Box>

      {rangeError && <Alert severity="warning" sx={{ mb: 1.5 }}>{rangeError}</Alert>}

      {/* Summary */}
      {!rangeError && !loading && !error && (
        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))", gap: 1.5, mb: 1.5 }}>
          {[
            ["Employees", filteredEmployees.length],
            ["Total Present Days", totalPresentDays],
            ["Days in Range", days.length],
          ].map(([label, value]) => (
            <Box key={label} sx={{
              background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)", border: "1px solid #aec4e0",
              p: "10px 14px", borderRadius: "12px", textAlign: "center", color: "#0E3C66", fontWeight: 600,
            }}>
              <Typography variant="h5" fontWeight={700}>{value}</Typography>
              <Typography variant="caption" sx={{ fontSize: "11px" }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Loading / error / empty */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress sx={{ color: NAVY }} />
          <Typography sx={{ ml: 2, color: NAVY, fontWeight: 500 }}>Loading attendance data...</Typography>
        </Box>
      )}
      {!loading && error && (
        <Paper sx={{ p: 3, textAlign: "center", backgroundColor: "#fff3f3", border: "1px solid #ffcccc", borderRadius: "10px" }}>
          <Typography color="error" fontWeight={600}>⚠️ Error: {error}</Typography>
          <Button variant="contained" onClick={handleRefresh} sx={{ mt: 1.5, backgroundColor: NAVY }}>Try Again</Button>
        </Paper>
      )}
      {!loading && !error && !rangeError && filteredEmployees.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: "10px" }}>
          <Typography sx={{ color: "#666" }}>No attendance records found for this selection.</Typography>
        </Paper>
      )}

      {/* Grid: employees x days */}
      {!loading && !error && !rangeError && filteredEmployees.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{ width: "100%", maxWidth: "100%", maxHeight: 560, overflowX: "auto", borderRadius: "10px", border: "1px solid #d1d9e6" }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 300 + days.length * 46 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, position: "sticky", left: 0, zIndex: 4, minWidth: 64, maxWidth: 64 }}>Service No</TableCell>
                <TableCell sx={{ ...headSx, textAlign: "left", pl: "16px", position: "sticky", left: 64, zIndex: 4, minWidth: 170 }}>Name</TableCell>
                {!loc && <TableCell sx={{ ...headSx, textAlign: "left", pl: "16px" }}>Location</TableCell>}
                {days.map((k) => (
                  <TableCell
                    key={k} align="center"
                    sx={{ ...headSx, minWidth: 44, p: "4px 2px", backgroundColor: isSunday(k) ? "#b45309" : NAVY }}
                  >
                    <div>{Number(k.slice(8))}</div>
                    <div style={{ fontSize: 9, fontWeight: 400 }}>{WEEKDAY[fromKey(k).getDay()]}</div>
                  </TableCell>
                ))}
                <TableCell align="center" sx={headSx}>Days</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.map((e) => (
                <TableRow key={e.serviceNo} hover>
                  <TableCell align="center" sx={{ position: "sticky", left: 0, zIndex: 1, backgroundColor: "#fff", fontSize: 12, fontWeight: 600, minWidth: 64, maxWidth: 64 }}>
                    {e.serviceNo}
                  </TableCell>
                  <TableCell sx={{ position: "sticky", left: 64, zIndex: 1, backgroundColor: "#fff", fontSize: 12, minWidth: 170 }}>
                    {e.name}
                  </TableCell>
                  {!loc && <TableCell sx={{ fontSize: 11, color: "#555", whiteSpace: "nowrap" }}>{e.locName}</TableCell>}
                  {days.map((k) => {
                    const d = e.days[k];
                    return (
                      <TableCell
                        key={k} align="center"
                        sx={{ p: "2px", fontSize: 10, lineHeight: 1.25, backgroundColor: isSunday(k) ? "#fff7ed" : "inherit", color: d?.inTime ? "#1b5e20" : "#999" }}
                      >
                        {d ? (d.inTime ? (<><div>{d.inTime}</div><div style={{ color: "#b71c1c" }}>{d.outTime || "—"}</div></>) : "—") : ""}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 12 }}>{e.present}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}